import { useState } from "react";
import { CellState } from "../types";
import { computeStars } from "../lib/constants";
import { formatDuration } from "../lib/format";
import { useFocusTrap } from "../hooks/useFocusTrap";
import styles from "./WinModal.module.css";

interface WinModalProps {
  swapsUsed: number;
  swapsRemaining: number;
  maxSwaps: number;
  startedAt: number;
  solvedAt: number | null;
  focusedMs: number;
  date: string;
  size: "7x7" | "9x9";
  cellStates: CellState[][];
  winStreak: number;
  longestWinStreak: number;
  onClose: () => void;
  onRetry?: () => void;
  onStats?: () => void;
  onLeaderboard?: () => void;
  timeRank?: number | null;
  onPrevUnsolved?: () => void;
  onNextUnsolved?: () => void;
  nextAction?: { label: string; onClick: () => void };
}

function StarRating({ stars }: { stars: number }) {
  const filled = Math.min(Math.max(stars, 0), 6);
  const empty = 6 - filled;
  return (
    <div className={styles.stars} aria-label={`${filled} out of 6 stars`}>
      {Array.from({ length: filled }, (_, i) => (
        <span key={`f${i}`} className={styles.starFilled}>&#9733;</span>
      ))}
      {Array.from({ length: empty }, (_, i) => (
        <span key={`e${i}`} className={styles.starEmpty}>&#9733;</span>
      ))}
    </div>
  );
}

function buildEmojiGrid(cellStates: CellState[][]): string {
  return cellStates
    .map((row) =>
      row
        .map((cell) => {
          if (cell.letter === null) return "\u2B1B";
          return "\uD83D\uDFE9";
        })
        .join("")
    )
    .join("\n");
}

export function WinModal({
  swapsUsed,
  swapsRemaining,
  maxSwaps,
  startedAt,
  solvedAt,
  focusedMs,
  date,
  size,
  cellStates,
  winStreak,
  longestWinStreak,
  onClose,
  onRetry,
  onStats,
  onLeaderboard,
  timeRank,
  onPrevUnsolved,
  onNextUnsolved,
  nextAction,
}: WinModalProps) {
  const [copied, setCopied] = useState(false);
  const trapRef = useFocusTrap<HTMLDivElement>();
  const overLimit = swapsRemaining < 0;
  const overBy = Math.abs(swapsRemaining);
  const stars = computeStars(swapsRemaining);

  const rating = overLimit
    ? "Solved!"
    : stars === 6
      ? "Perfect!"
      : stars >= 5
        ? "Brilliant!"
        : stars >= 4
          ? "Excellent!"
          : stars >= 3
            ? "Great!"
            : stars >= 2
              ? "Good!"
              : stars >= 1
                ? "Close!"
                : "Solved!";

  const starEmoji = Array.from({ length: Math.min(stars, 6) }, () => "\u2B50").join("");
  const emptyStarEmoji = Array.from({ length: 6 - Math.min(stars, 6) }, () => "\u2606").join("");
  const emojiGrid = buildEmojiGrid(cellStates);
  const dateLabel = date === "random" ? "Random" : date;

  const puzzleUrl = date === "random" ? "https://griddle.gg" : `https://griddle.gg/${size}/${date}`;

  const streakLine = !overLimit && winStreak > 1
    ? winStreak >= longestWinStreak
      ? `\uD83D\uDD25 ${winStreak} win streak (new best!)`
      : `\uD83D\uDD25 ${winStreak} win streak`
    : null;

  const shareText = overLimit
    ? [
        `Griddle ${size} - ${dateLabel}`,
        `Solved in ${swapsUsed} swaps (${overBy} over limit)`,
        "",
        emojiGrid,
        "",
        puzzleUrl,
      ].join("\n")
    : [
        `Griddle ${size} - ${dateLabel}`,
        `${starEmoji}${emptyStarEmoji} (${stars}/6)`,
        `Swaps: ${swapsUsed}/${maxSwaps}`,
        ...(streakLine ? [streakLine] : []),
        "",
        emojiGrid,
        "",
        puzzleUrl,
      ].join("\n");

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      try {
        if (navigator.share) {
          await navigator.share({ text: shareText });
        }
      } catch { /* cancelled or unavailable */ }
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose} onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>
      <div className={styles.modal} role="dialog" aria-modal="true" ref={trapRef} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeX} onClick={onClose} aria-label="Close" autoFocus>&times;</button>
        <h2 className={overLimit ? styles.ratingOver : styles.rating}>{rating}</h2>
        {!overLimit && <StarRating stars={stars} />}
        <p className={styles.stats}>
          {overLimit ? (
            <>
              You solved the puzzle in <strong>{swapsUsed}</strong> swaps
              <br />
              <strong>{overBy}</strong> more than allowed
            </>
          ) : (
            <>
              You solved the puzzle in <strong>{swapsUsed}</strong> swaps
              <br />
              with <strong>{swapsRemaining}</strong> remaining
            </>
          )}
          <br />
          {(() => {
            const wallMs = (solvedAt ?? Date.now()) - startedAt;
            if (!focusedMs || formatDuration(focusedMs) === formatDuration(wallMs)) {
              return <span className={styles.time}>Time: {formatDuration(wallMs)}</span>;
            }
            return (
              <>
                <span className={styles.time}>Active: {formatDuration(focusedMs)}</span>
                <br />
                <span className={styles.timeSecondary}>Wall: {formatDuration(wallMs)}</span>
              </>
            );
          })()}
        </p>
        {!overLimit && winStreak > 1 && (
          <p className={styles.streak}>
            {"\uD83D\uDD25"} {winStreak} win streak{winStreak >= longestWinStreak ? " — new best!" : ""}
          </p>
        )}
        {(onPrevUnsolved || onNextUnsolved) && (
          <div className={styles.navRow}>
            <button
              className={`${styles.navButton}${onPrevUnsolved ? "" : ` ${styles.navHidden}`}`}
              onClick={onPrevUnsolved}
              aria-label="Previous unsolved puzzle"
            >
              &laquo; Prev
            </button>
            <button
              className={`${styles.navButton}${onNextUnsolved ? "" : ` ${styles.navHidden}`}`}
              onClick={onNextUnsolved}
              aria-label="Next unsolved puzzle"
            >
              Next &raquo;
            </button>
          </div>
        )}
        <div className={styles.actions}>
          {!overLimit && (
            <button className={styles.shareButton} onClick={handleShare}>
              {copied ? "Copied!" : "Share"}
            </button>
          )}
          {onRetry && (
            <button className={styles.keepPlayingButton} onClick={onRetry}>
              Try Again
            </button>
          )}
          {nextAction && (
            <button className={styles.newGameButton} onClick={nextAction.onClick}>
              {nextAction.label}
            </button>
          )}
        </div>
        {(onStats || onLeaderboard) && (
          <div className={styles.linkRow}>
            {onStats && <button className={styles.linkButton} onClick={onStats}>Stats</button>}
            {onLeaderboard && (
              <button className={styles.linkButton} onClick={onLeaderboard}>
                {timeRank ? `#${timeRank} on Leaderboard` : "Leaderboard"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
